import { useEffect } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { IS_LOCALHOST, IS_PLG_PROD } from 'constants/Global.constants';

import { AccountType } from 'api/resources/accounts/types';
import useAccount from 'hooks/useAccount';
import { useCurrentUserTags } from 'hooks/useCurrentUserTags';
import { isPolyEmail } from 'hooks/useProjectResources';
import { updatePendo } from 'lib/pendo';
import { useTrackingConsent } from './useTrackingConsent';

interface UsePendoOptions {
  projectId?: string;
  accountId?: string;
  additionalInfo?: Record<string, any>;
}

export const usePendo = ({
  projectId,
  accountId,
  additionalInfo,
}: UsePendoOptions = {}) => {
  const { user } = useAuth0();
  const { account } = useAccount();
  const { hasStatisticsConsent, isConsentReady } = useTrackingConsent();
  // Switched to environmental variable check for PLG prod since the feature flag
  // check breaks access to public pages when the user hasn't logged in, which the
  // OT integration flow relies on.
  const isPlgProd = IS_PLG_PROD;

  const userTags = useCurrentUserTags({ accountId, isEnabled: !!accountId });
  const accountName = account?.name ?? '';
  const accountType =
    account?.conversation_limits.account_type ?? AccountType.STANDARD;

  const pendoKey = process.env.NEXT_PUBLIC_PENDO_APP_ID;

  useEffect(() => {
    // Don't initialize Pendo if user hasn't consented to statistics cookies
    if (!pendoKey || !isConsentReady || !hasStatisticsConsent || IS_LOCALHOST) {
      return;
    }

    // Use email for authenticated users; empty string for anonymous users so Pendo
    // manages its own anonymous ID and can correctly merge sessions after login.
    // (Setting a custom anonymous ID prevents Pendo from merging on authentication.)
    // refs:
    // https://support.pendo.io/hc/en-us/articles/360032202751-Anonymous-visitors
    // https://support.pendo.io/hc/en-us/articles/21202637874587-Identity-mapping-overview
    const visitorId = user?.email ?? '';

    const visitor = {
      id: visitorId,
      environment: process.env.NEXT_PUBLIC_POLYAI_ENVIRONMENT || '',
      region: process.env.NEXT_PUBLIC_POLYAI_REGION || '',
      userType:
        user?.email && isPolyEmail(user.email) ? 'internal' : 'external',
      isPlg: isPlgProd,
      ...(user?.email && { email: user.email }), // Add email as metadata when available
      ...(projectId && { project_id: projectId }),
      account_type: accountType,
      ...(userTags && userTags.length > 0 && { tags: userTags }),
      ...(additionalInfo && { ...additionalInfo }),
    };

    const pendoAccount = accountId
      ? { id: accountId, name: accountName }
      : { id: 'before-sign-in' };

    updatePendo(pendoKey, visitor, pendoAccount);
  }, [
    pendoKey,
    user?.email,
    projectId,
    accountId,
    accountName,
    accountType,
    userTags,
    hasStatisticsConsent,
    isConsentReady,
    isPlgProd,
    additionalInfo,
  ]);
};
